import { pick } from 'lodash';
import { Injectable } from '@angular/core';
import { Random, Command, StateService } from 'flux-core';
import { IConnectorPoint, IConnectorEndPoint, DEFAULT_CONNECTOR } from 'flux-diagram-composer';
import { INewConnectorConnection } from '../../../base/shape/connection/connection.i';
import { ConnectorModel } from '../../../base/shape/model/connector.mdl';
import { AbstractDiagramChangeCommand } from './abstract-diagram-change-command.cmd';
import { DiagramChangeService } from '../../../base/diagram/diagram-change.svc';
import { ConnectorBarService } from '../connector-bar/connector-bar.svc';
import { DefinitionLocator } from '../../../base/shape/definition/definition-locator.svc';
import { TranslateService } from '@ngx-translate/core';
import { translateSingleRunContent } from './utils/shape-text-translation.util';
import { take, tap } from 'rxjs/operators';

/**
 * AddDiagramConnector
 * Adds a new connector on the diagram with given points. It will also try to select
 * the best type for the connector by the shapes the connector is connected to.
 */
@Injectable()
@Command()
export class AddConnector extends AbstractDiagramChangeCommand {
    /**
     * Command input data format
     * The entryClass is the full class identifier as used in shape definitions.
     */
    public data: {
        id: string,
        points: IConnectorPoint[],
        defId?: any,
        version?: any,
        style?: any,
        texts?: any,
        entryClass?: string,
        connectorToCopyStylesFrom?: ConnectorModel,
    };
    constructor(
        protected ds: DiagramChangeService,
        protected state: StateService<any, any>,
        protected connectorBarService: ConnectorBarService,
        protected defLocator: DefinitionLocator,
        protected translate: TranslateService,
    ) {
        super( ds );
    }
    /**
     * Prepare command data by modifying the change model.
     */
    public prepareData(): any {
        const toCopy = this.data.connectorToCopyStylesFrom || {} as ConnectorModel;
        const otherData = pick( this.data, [ 'defId', 'version', 'entryClass', 'style', 'texts' ]);
        const connection = this.prepareConnection( this.data.points );

        const shapeAId = connection?.connection?.shapeA?.shapeId;
            // || ( this.data.points?.[0] as any )?.shapeId;
        const shapeBId = connection?.connection?.shapeB?.shapeId;
            // || ( this.data.points?.[1] as any )?.shapeId;

        if ( shapeAId && shapeBId && this.shouldAddConnectorBar( shapeAId , shapeBId )) {
            const result = this.connectorBarService.addConnectorBar(shapeAId, shapeBId, this.changeModel);
            if (result) {
                return result;
            }
        }

        this.resultData = { id: this.data.id };
        const defId = connection?.definitions[0].defId;
        const connector = this.prepareConnector(
            this.data.id,
            this.data.points,
            otherData,
            ( !defId && toCopy.defId === 'creately.connector.default' ) ? toCopy :
                defId && defId === toCopy.defId ? toCopy : {} as ConnectorModel,
        );
        if ( connection ) {
            const definition = connection.definitions[0];
            connector.defId = definition.defId;
            connector.version = definition.version;
            connector.connectionId = Random.connectionId();
            this.changeModel.connections[connector.connectionId] = connection.connection;

            // Merge connector texts from def when created via plus-create path,
            // and translate single-run content (same logic as add-diagram-shape)
            this.defLocator.getDefinition( connector.defId ).pipe(
                take( 1 ),
                tap(( def: any ) => {
                    if ( ( !connector.texts || Object.keys( connector.texts ).length === 0 ) && def?.texts ) {
                        const merged: any = {};
                        Object.keys( def.texts ).forEach( tid => {
                            merged[tid] = { ...def.texts[tid], id: tid } as any;
                            translateSingleRunContent( this.translate, merged[tid] );
                        });
                        connector.texts = merged;
                    }
                }),
            ).subscribe();
        }
        connector.visible = true; // This is because the connector we copy style from might be hidden
        this.changeModel.shapes[connector.id] = connector;
    }

    protected shouldAddConnectorBar( shapeAId, shapeBId ) {
        return [
            this.changeModel.shapes[ shapeAId ]?.defId,
            this.changeModel.shapes[ shapeBId ]?.defId,
        ].every( val => val.includes( 'creately.genogram' ));
    }

    /**
     * Prepares connector model data
     */
    private prepareConnector( id: string, points: IConnectorPoint[], otherData: object, toCopy: ConnectorModel ): any {
        return {
            ...toCopy,
            id: id,
            texts: {},
            type: 'connector',
            hidden: false,
            defId: DEFAULT_CONNECTOR.defId,
            version: DEFAULT_CONNECTOR.version,
            path: ConnectorModel.createPathFromPoints( points ),
            zIndex: this.changeModel.maxZIndex + 1,
            ...( toCopy?.style && {
                style: {
                    ...toCopy.style,
                    lineColor: toCopy.style?.lineColor && this.rgbaToHex( toCopy.style.lineColor ),
                },
            }),
            ...otherData,
            createdBy: {
                userId: this.state.get( 'CurrentUser' ),
                time: new Date().getTime(),
            },
        };
    }

    /**
     * Returns information to create a new connector connection for a connector.
     */
    private prepareConnection( points: IConnectorPoint[]): INewConnectorConnection {
        const shapeAId = ( points[0] as IConnectorEndPoint ).shapeId;
        const shapeBId = ( points[ points.length - 1 ] as IConnectorEndPoint ).shapeId;
        if ( !shapeAId || !shapeBId || shapeAId === shapeBId ) {
            return null;
        }
        // we only get potential connections if the shapes are flowchart shapes,
        // because only flowchart shapes have ports
        const connections = this.changeModel.getPotentialConnections( shapeAId, shapeBId )
            .filter( conn => conn.connection.type === 'connector' );
        return connections[0] || null;
    }

    /**
     * Converts an rgba color code to corresponding hex code.
     * It ignores the alpha value and uses just the rgb values.
     * Conversion is necessary if connectorToCopyStylesFrom it is hidden.
     * @param color
     */
    private rgbaToHex( color: string ) {
        const rgba = color.replace( /^rgba?\(|\s+|\)$/g, '' ).split( ',' );
        if ( rgba.length === 3 || rgba.length === 4 ) {
            // tslint:disable-next-line:no-bitwise
            const hex = `#${(( 1 << 24 ) + ( parseInt( rgba[ 0 ], 10 ) << 16 ) + ( parseInt( rgba[ 1 ], 10 ) << 8 ) + parseInt( rgba[ 2 ], 10 )).toString( 16 ).slice( 1 )}`;
            return hex;
        } else {
            return color;
        }
    }
}

// NOTE: class names are lost on minification
Object.defineProperty( AddConnector, 'name', {
    value: 'AddConnector',
});
